// src/components/NotificationDialog.js
import React from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Stack from '@mui/joy/Stack';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNotifications } from '../contexts/NotificationContext';
import { motion } from 'framer-motion';

function getNotificationIcon(type) {
  switch (type) {
    case 'warning':
      return <WarningIcon sx={{ color: '#ff9800' }} />;
    case 'error':
      return <ErrorIcon sx={{ color: '#f44336' }} />;
    case 'success':
      return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
    default:
      return <InfoIcon sx={{ color: '#2196f3' }} />;
  }
}

function getNotificationColor(type) {
  switch (type) {
    case 'warning': return '#ff9800';
    case 'error': return '#f44336';
    case 'success': return '#4caf50';
    default: return '#2196f3';
  }
}

const MotionBox = motion(Box);
const MotionCard = motion(Card);

export default function NotificationDialog({ open, onClose, notification }) {
  const { readOne, clearOne } = useNotifications();
  
  if (!notification) return null;

  const notificationType = notification.itemType?.includes('ERROR') ? 'error' : 'warning';
  const icon = getNotificationIcon(notificationType);
  const color = getNotificationColor(notificationType);

  const handleMarkAsRead = async () => {
    try {
      await readOne(notification.notificationId);
      onClose();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await clearOne(notification.notificationId);
      onClose();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  function handleClose() {
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <ModalDialog
        variant="soft"
        sx={{ 
          width: '100vw',
          height: '100vh',
          maxWidth: 'none',
          maxHeight: 'none',
          m: 0,
          borderRadius: 0,
          overflow: 'auto',
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.97) 0%, rgba(10, 10, 10, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: 'none'
        }}
      >
        <MotionBox 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          sx={{ p: { xs: 3, md: 6 }, pt: { xs: 8, md: 6 } }}
        >
          {/* Enhanced Header */}
          <Box sx={{ 
            textAlign: 'center', 
            mb: 6,
            position: 'relative'
          }}>
            {/* Background glow effect */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 200,
                height: 200,
                background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
                borderRadius: '50%',
                filter: 'blur(40px)',
                zIndex: 0
              }}
            />
            
            <Stack direction="column" alignItems="center" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
              <Avatar
                variant="soft"
                sx={{ 
                  width: 80, 
                  height: 80, 
                  bgcolor: `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.2)`,
                  border: `3px solid ${color}`,
                  boxShadow: `0 8px 32px rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.4)`
                }}
              >
                {React.cloneElement(icon, { sx: { fontSize: 40 } })}
              </Avatar>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography level="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                  {notification.name || 'Inventory Alert'}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                  <Chip 
                    variant="soft"
                    size="lg"
                    sx={{ 
                      bgcolor: `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.2)`,
                      color: color,
                      border: `1px solid ${color}`,
                      fontWeight: 'bold'
                    }}
                  >
                    {notificationType.toUpperCase()}
                  </Chip>
                  {!notification.read && (
                    <Chip 
                      variant="soft"
                      size="sm"
                      sx={{ 
                        bgcolor: 'rgba(255, 152, 0, 0.2)',
                        color: '#ff9800',
                        border: '1px solid rgba(255, 152, 0, 0.3)'
                      }}
                    >
                      UNREAD
                    </Chip>
                  )}
                </Stack>
              </Box>
            </Stack>
            
            <ModalClose 
              sx={{ 
                position: 'absolute',
                top: { xs: -20, md: -30 },
                right: { xs: -10, md: -20 },
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                zIndex: 10,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.1)'
                }
              }}
            />
          </Box>

          {/* Main Content Card */}
          <MotionCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            variant="soft"
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 'xl',
              mb: 4
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {/* Message Section */}
              <Box sx={{ mb: 4 }}>
                <Typography level="h4" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, color: color }} />
                  Notification Details
                </Typography>
                <Typography level="body-lg" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.6, mb: 2 }}>
                  {notification.message}
                </Typography>
                
                {notification.description && (
                  <Typography level="body-md" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.5 }}>
                    {notification.description}
                  </Typography>
                )}
              </Box>

              {/* Metadata */}
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {notification.createdAt ? new Date(notification.createdAt).toLocaleString() : 'Recent notification'}
                  </Typography>
                </Stack>
                
                <Stack direction="row" spacing={2} alignItems="center">
                  <InfoIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Item Type: {notification.itemType || 'Unknown'}
                  </Typography>
                </Stack>
              </Box>

              {/* Image Section */}
              {notification.imageUrl && (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography level="title-md" sx={{ color: 'white', mb: 2 }}>
                    Item Image
                  </Typography>
                  <Box
                    component="img"
                    src={notification.imageUrl}
                    alt={notification.name || 'Notification image'}
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      height: 'auto',
                      borderRadius: 'lg',
                      border: `2px solid ${color}`,
                      boxShadow: `0 8px 32px rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.3)`
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </MotionCard>
          
          {/* Action Buttons */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            alignItems="center"
          >
            {!notification.read && (
              <Button 
                variant="outlined"
                startDecorator={<MarkAsUnreadIcon />}
                onClick={handleMarkAsRead}
                sx={{
                  borderColor: color,
                  color: color,
                  borderRadius: 'lg',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    bgcolor: `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.1)`,
                    borderColor: color
                  }
                }}
              >
                Mark as Read
              </Button>
            )}
            
            <Button 
              variant="outlined"
              startDecorator={<DeleteIcon />}
              onClick={handleDelete}
              sx={{
                borderColor: '#f44336',
                color: '#f44336',
                borderRadius: 'lg',
                px: 3,
                py: 1,
                '&:hover': {
                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                  borderColor: '#f44336'
                }
              }}
            >
              Delete
            </Button>
            
            <Button 
              variant="solid"
              onClick={handleClose}
              sx={{
                bgcolor: '#ff9800',
                borderRadius: 'lg',
                px: 4,
                py: 1,
                boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)',
                '&:hover': {
                  bgcolor: '#e65100',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(255, 152, 0, 0.4)'
                }
              }}
            >
              Close
            </Button>
          </Stack>
        </MotionBox>
      </ModalDialog>
    </Modal>
  );
}
